import React, { useContext, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { filterMaybeArray } from "@examspark/common";
import { Button, HSpace, SearchField, useDebounce } from "@examspark/common-ui";
import {
  IconFilterLine,
  IconRejectLine,
  IconSearchLine,
} from "@examspark/common-ui/dist/components/icons";
import { Resource } from "@examspark/types";

import { NewActivityContext } from "../../../../classroom";
import { useQuerySearchItemFacets } from "../hooks/useItemFacets";
import { useQuerySearchItems } from "../hooks/useSearch";

const ToolbarPane = styled(HSpace)`
  padding: 1rem;
  /* background-color: #f7f7f7; */
  border-bottom: solid 1px #ddd;
`;

type Facet = {
  label: string;
  value: string;
  count: string;
};

type ObjectiveFacet = Facet & {
  sequence: number;
};

// This component is responsible for controlling not just
// the search filters, but the search itself and the results
type ActivityEditorItemPickerSearchToolbarProps = {
  results: Resource[];
  setResults: (results: Resource[]) => void;
};

/** @deprecated this component has been largely moved into `ItemPickerMain` */
export const ItemPickerSearchToolbar = ({
  setResults,
}: ActivityEditorItemPickerSearchToolbarProps) => {
  const [state] = useContext(NewActivityContext);
  const [searchString, setSearchString] = useState("");
  const [searchStringId, setSearchStringId] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>();
  const [, setTypeFacets] = useState<Facet[]>();
  const [, setObjectiveFacets] = useState<ObjectiveFacet[]>();
  const [objectiveFilter, setObjectiveFilter] = useState("");
  const [, setPassageFacets] = useState<Facet[]>();
  const [passageFilter, setPassageFilter] = useState("");

  const clearSearch = () => {
    setSearchString("");
    setSearchStringId("");
    setTypeFilter("");
    setObjectiveFilter("");
    setPassageFilter("");
  };

  //debounce
  const filters = useMemo(() => {
    return {
      query: searchString,
      queryId: searchStringId,
      objectiveIds: objectiveFilter ? [objectiveFilter] : undefined,
      language: state.language,
      interactionTypes: typeFilter
        ? [typeFilter]
        : state.interactionTypes || [],
      contextIds: passageFilter ? [passageFilter] : undefined,
    };
  }, [
    searchString,
    searchStringId,
    objectiveFilter,
    state.language,
    state.interactionTypes,
    typeFilter,
    passageFilter,
  ]);
  const debouncedFilters = useDebounce(filters, 500);

  const searchQuery = useQuerySearchItems(debouncedFilters);
  useEffect(() => {
    if (searchQuery.data) {
      setResults(filterMaybeArray(searchQuery.data?.items));
    }
  }, [searchQuery.data, setResults]);

  const facetsQuery = useQuerySearchItemFacets(debouncedFilters);

  useEffect(() => {
    if (facetsQuery.data) {
      setObjectiveFacets(facetsQuery.data?.objectives);
      setTypeFacets(facetsQuery.data?.interactions);
      setPassageFacets(facetsQuery.data?.passages);
    }
  }, [facetsQuery.data]);

  return (
    <>
      <ToolbarPane>
        <Button title="Filter" icon={<IconFilterLine />} variant="outlined" />
        <div style={{ flex: "1" }}>
          <SearchField
            placeholder="Search by Name"
            value={searchString}
            onChange={(value) => {
              setSearchString(value);
            }}
            onEnterKeyPress={() => {
              searchQuery.refetch();
            }}
          />
        </div>
        <Button
          title="Clear Search"
          variant="outlined"
          onClick={() => clearSearch()}
          isLoading={searchQuery.isLoading}
          icon={<IconRejectLine />}
        >
          Clear
        </Button>
        <Button
          title="Refresh Search"
          variant="outlined"
          onClick={() => searchQuery.refetch()}
          isLoading={searchQuery.isLoading}
          icon={<IconSearchLine />}
        />
      </ToolbarPane>
    </>
  );
};
